/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import {
  SafeAreaView,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  StatusBar,
  Modal,
  TextInput,
  Alert,
  ActivityIndicator,
  Image,
} from 'react-native';
import {
  Ionicons,
  MaterialCommunityIcons,
  FontAwesome,
  AntDesign,
} from '@expo/vector-icons';
import { useNavigation, useRoute } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import axios from 'axios';

// ==================== ✅ API CONFIGURATION ====================
const API_BASE_URL = 'https://api.jobeng.ai';
const API_ENDPOINTS = {
  projects: `${API_BASE_URL}/api/projects`,
  companyProfile: `${API_BASE_URL}/api/company/profile`,
};

// ✅ Axios instance with API key and auth interceptor
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef',
  },
});

api.interceptors.request.use(
  async (config) => {
    try {
      const token = await AsyncStorage.getItem('auth_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      const companyId = await AsyncStorage.getItem('company_id')
                      || await AsyncStorage.getItem('company_id');
      if (companyId) {
        config.params = { ...config.params, company_id: parseInt(companyId), company_id: parseInt(companyId) };
      }
    } catch (error) {
      console.error('Error in request interceptor:', error);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

export default function ProjectRisksScreen() {
  const navigation = useNavigation();
  const route = useRoute();
  const { project } = route.params;

  const [loading, setLoading] = useState(false);
  const [showAddRiskModal, setShowAddRiskModal] = useState(false);
  const [showEditRiskModal, setShowEditRiskModal] = useState(false);
  const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
  const [selectedRisk, setSelectedRisk] = useState(null);
  const [risk, setRisk] = useState({
    description: '',
    severity: 'Medium',
    mitigation: '',
  });
  const [projectData, setProjectData] = useState(null);
  const [filterSeverity, setFilterSeverity] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [companyLogo, setCompanyLogo] = useState(null);

  // Risk severity options
  const severityOptions = ['Low', 'Medium', 'High'];
  const filterOptions = ['All', ...severityOptions];

  // Load company logo on mount
  useEffect(() => {
    loadCompanyLogo();
  }, []);

  const loadCompanyLogo = async () => {
    try {
      const cachedProfile = await AsyncStorage.getItem('company_profile');
      if (cachedProfile) {
        const profile = JSON.parse(cachedProfile);
        setCompanyLogo(profile.logo || null);
      }

      const companyId = await AsyncStorage.getItem('company_id');
      if (companyId) {
        const response = await api.get(API_ENDPOINTS.companyProfile, {
          params: { company_id: companyId },
        });
        if (response.data?.success && response.data.data && response.data.data.length > 0) {
          const latestProfile = response.data.data[0];
          setCompanyLogo(latestProfile.logo || null);
          await AsyncStorage.setItem('company_profile', JSON.stringify(latestProfile));
        }
      }
    } catch (error) {
      console.warn('Error loading company logo:', error);
    }
  };

  // Load project data on component mount
  useEffect(() => {
    if (project) {
      setProjectData(project);
    }
  }, [project]);

  // Handle back button navigation
  const handleGoBack = () => {
    navigation.goBack();
  };

  // Get severity color
  const getSeverityColor = severity => {
    switch (severity) {
      case 'High':
        return '#EF4444';
      case 'Medium':
        return '#F59E0B';
      case 'Low':
        return '#10B981';
      default:
        return '#64748B';
    }
  };

  // Handle add risk
  const handleAddRisk = () => {
    if (risk.description.trim() === '' || risk.mitigation.trim() === '') {
      Alert.alert('Error', 'Please enter both description and mitigation for the risk');
      return;
    }

    const newRisk = {
      ...risk,
      id: `r${Date.now()}`,
    };

    const updatedProject = {
      ...projectData,
      risks: [...projectData.risks, newRisk],
    };

    setProjectData(updatedProject);
    setRisk({ description: '', severity: 'Medium', mitigation: '' });
    setShowAddRiskModal(false);

    Alert.alert('Success', 'Risk added successfully');
  };

  // Handle edit risk
  const handleEditRisk = () => {
    if (risk.description.trim() === '' || risk.mitigation.trim() === '') {
      Alert.alert('Error', 'Please enter both description and mitigation for the risk');
      return;
    }

    const updatedRisks = projectData.risks.map(item =>
      item.id === selectedRisk.id ? { ...risk, id: item.id } : item
    );

    const updatedProject = {
      ...projectData,
      risks: updatedRisks,
    };

    setProjectData(updatedProject);
    setRisk({ description: '', severity: 'Medium', mitigation: '' });
    setSelectedRisk(null);
    setShowEditRiskModal(false);

    Alert.alert('Success', 'Risk updated successfully');
  };

  // Handle delete risk
  const handleDeleteRisk = () => {
    const updatedRisks = projectData.risks.filter(item => item.id !== selectedRisk.id);
    const updatedProject = { ...projectData, risks: updatedRisks };
    setProjectData(updatedProject);
    setSelectedRisk(null);
    setShowConfirmDeleteModal(false);
    Alert.alert('Success', 'Risk deleted successfully');
  };

  // Open edit risk modal
  const openEditRiskModal = risk => {
    setSelectedRisk(risk);
    setRisk({
      description: risk.description,
      severity: risk.severity,
      mitigation: risk.mitigation,
    });
    setShowEditRiskModal(true);
  };

  // Open delete risk confirmation
  const openDeleteRiskConfirmation = risk => {
    setSelectedRisk(risk);
    setShowConfirmDeleteModal(true);
  };

  // Filter risks by severity and search query
  const getFilteredRisks = () => {
    if (!projectData || !projectData.risks) return [];

    let filtered = projectData.risks;

    if (filterSeverity !== 'All') {
      filtered = filtered.filter(risk => risk.severity === filterSeverity);
    }

    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        risk =>
          String(risk.description ?? '').toLowerCase().includes(query) ||
          String(risk.mitigation ?? '').toLowerCase().includes(query)
      );
    }

    return filtered;
  };

  // Sort risks by severity (High to Low)
  const getSortedRisks = () => {
    const filtered = getFilteredRisks();
    return filtered.sort((a, b) => {
      const severityOrder = { High: 0, Medium: 1, Low: 2 };
      return severityOrder[a.severity] - severityOrder[b.severity];
    });
  };

  // Calculate risk statistics
  const calculateRiskStats = () => {
    if (!projectData || !projectData.risks || projectData.risks.length === 0) {
      return { high: 0, medium: 0, low: 0, total: 0 };
    }
    const high = projectData.risks.filter(risk => risk.severity === 'High').length;
    const medium = projectData.risks.filter(risk => risk.severity === 'Medium').length;
    const low = projectData.risks.filter(risk => risk.severity === 'Low').length;
    return { high, medium, low, total: high + medium + low };
  };

  const riskStats = calculateRiskStats();

  if (!projectData) {
    return (
      <SafeAreaView style={styles.safeArea}>
        <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />
        <View style={styles.loadingContainer}>
          <ActivityIndicator size={36} color="#0e4e96" />
          <Text style={styles.loadingText}>Loading risk assessment...</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />

      {/* Header with Back Button, Company Logo, and Title */}
      <View style={styles.header}>
        <TouchableOpacity style={styles.backButton} onPress={handleGoBack}>
          <Ionicons name="chevron-back" size={24} color="#fff" />
        </TouchableOpacity>

        <View style={styles.headerCenter}>
          {companyLogo ? (
            <Image source={{ uri: companyLogo || undefined }} style={styles.headerLogo} />
          ) : (
            <MaterialCommunityIcons name="office-building" size={30} color="#fff" />
          )}
          <Text style={styles.headerTitle}>Risk Assessment</Text>
        </View>

        <View style={{ width: 30 }} />
      </View>

      {/* Project Title */}
      <View style={styles.projectHeader}>
        <Text style={styles.projectTitle}>{projectData.title}</Text>
      </View>

      {/* Risk Summary */}
      <View style={styles.riskSummary}>
        <View style={styles.riskStatRow}>
          <View style={[styles.riskStat, { backgroundColor: '#FEE2E2' }]}>
            <Text style={[styles.riskStatValue, { color: '#B91C1C' }]}>{riskStats.high}</Text>
            <Text style={[styles.riskStatLabel, { color: '#B91C1C' }]}>High</Text>
          </View>
          <View style={[styles.riskStat, { backgroundColor: '#FEF3C7' }]}>
            <Text style={[styles.riskStatValue, { color: '#B45309' }]}>{riskStats.medium}</Text>
            <Text style={[styles.riskStatLabel, { color: '#B45309' }]}>Medium</Text>
          </View>
          <View style={[styles.riskStat, { backgroundColor: '#DCFCE7' }]}>
            <Text style={[styles.riskStatValue, { color: '#166534' }]}>{riskStats.low}</Text>
            <Text style={[styles.riskStatLabel, { color: '#166534' }]}>Low</Text>
          </View>
        </View>
        <View style={styles.riskStatTotal}>
          <Text style={styles.riskStatTotalValue}>{riskStats.total}</Text>
          <Text style={styles.riskStatTotalLabel}>Total Risks</Text>
        </View>
      </View>

      {/* Search and Filter */}
      <View style={styles.searchFilterContainer}>
        <View style={styles.searchContainer}>
          <Ionicons name="search" size={20} color="#64748B" />
          <TextInput
            style={styles.searchInput}
            placeholder="Search risks..."
            placeholderTextColor="#94A3B8"
            value={searchQuery}
            onChangeText={setSearchQuery}
          />
          {searchQuery.length > 0 && (
            <TouchableOpacity onPress={() => setSearchQuery('')}>
              <Ionicons name="close-circle" size={20} color="#64748B" />
            </TouchableOpacity>
          )}
        </View>

        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.filterContainer}>
          {filterOptions.map(severity => (
            <TouchableOpacity
              key={severity}
              style={[
                styles.filterOption,
                filterSeverity === severity && styles.filterOptionActive,
                filterSeverity === severity && severity !== 'All' && {
                  backgroundColor: getSeverityColor(severity) + '20',
                  borderColor: getSeverityColor(severity),
                },
              ]}
              onPress={() => setFilterSeverity(severity)}
            >
              {severity !== 'All' && (
                <View style={[styles.severityDot, { backgroundColor: getSeverityColor(severity) }]} />
              )}
              <Text
                style={[
                  styles.filterOptionText,
                  filterSeverity === severity && styles.filterOptionTextActive,
                  filterSeverity === severity && severity !== 'All' && { color: getSeverityColor(severity) },
                ]}
              >
                {severity}
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>

      {/* Add Risk Button */}
      <View style={styles.addButtonContainer}>
        <TouchableOpacity style={styles.addButton} onPress={() => setShowAddRiskModal(true)}>
          <Ionicons name="add" size={20} color="#fff" />
          <Text style={styles.addButtonText}>Add Risk</Text>
        </TouchableOpacity>
      </View>

      {/* Risks List */}
      <ScrollView style={styles.scrollContainer} contentContainerStyle={styles.contentContainer}>
        {getSortedRisks().length === 0 ? (
          <View style={styles.emptyState}>
            <MaterialCommunityIcons name="alert-circle-outline" size={40} color="#CBD5E1" />
            <Text style={styles.emptyStateText}>
              {filterSeverity === 'All' && searchQuery === '' ? 'No risks added yet' : 'No risks match your filters'}
            </Text>
          </View>
        ) : (
          getSortedRisks().map(risk => (
            <View key={risk.id} style={styles.riskCard}>
              <View style={styles.riskHeader}>
                <View
                  style={[
                    styles.severityBadge,
                    {
                      backgroundColor:
                        risk.severity === 'High' ? '#FEE2E2' : risk.severity === 'Medium' ? '#FEF3C7' : '#DCFCE7',
                    },
                  ]}
                >
                  <Text
                    style={[
                      styles.severityText,
                      {
                        color:
                          risk.severity === 'High' ? '#B91C1C' : risk.severity === 'Medium' ? '#B45309' : '#166534',
                      },
                    ]}
                  >
                    {risk.severity}
                  </Text>
                </View>
                <View style={styles.riskActions}>
                  <TouchableOpacity style={styles.riskAction} onPress={() => openEditRiskModal(risk)}>
                    <Ionicons name="create-outline" size={20} color="#64748B" />
                  </TouchableOpacity>
                  <TouchableOpacity style={styles.riskAction} onPress={() => openDeleteRiskConfirmation(risk)}>
                    <Ionicons name="trash-outline" size={20} color="#64748B" />
                  </TouchableOpacity>
                </View>
              </View>
              <Text style={styles.riskDescription}>{risk.description}</Text>
              <View style={styles.riskMitigation}>
                <Text style={styles.riskMitigationLabel}>Mitigation Plan:</Text>
                <Text style={styles.riskMitigationText}>{risk.mitigation}</Text>
              </View>
            </View>
          ))
        )}
      </ScrollView>

      {/* Add Risk Modal */}
      <Modal
        animationType="slide"
        transparent={true}
        visible={showAddRiskModal}
        onRequestClose={() => setShowAddRiskModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Add Risk</Text>
              <TouchableOpacity style={styles.modalCloseButton} onPress={() => setShowAddRiskModal(false)}>
                <Ionicons name="close" size={24} color="#64748B" />
              </TouchableOpacity>
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>Description *</Text>
              <TextInput
                style={[styles.input, styles.textArea]}
                placeholder="Enter risk description"
                placeholderTextColor="#94A3B8"
                value={risk.description}
                onChangeText={text => setRisk({ ...risk, description: text })}
                multiline
                numberOfLines={3}
                textAlignVertical="top"
              />
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>Severity</Text>
              <View style={styles.optionsContainer}>
                {severityOptions.map(severity => (
                  <TouchableOpacity
                    key={severity}
                    style={[
                      styles.optionButton,
                      risk.severity === severity && styles.optionButtonActive,
                      risk.severity === severity && { borderColor: getSeverityColor(severity) },
                    ]}
                    onPress={() => setRisk({ ...risk, severity: severity })}
                  >
                    <View style={[styles.severityDot, { backgroundColor: getSeverityColor(severity) }]} />
                    <Text
                      style={[
                        styles.optionButtonText,
                        risk.severity === severity && styles.optionButtonTextActive,
                        risk.severity === severity && { color: getSeverityColor(severity) },
                      ]}
                    >
                      {severity}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>Mitigation Plan *</Text>
              <TextInput
                style={[styles.input, styles.textArea]}
                placeholder="Enter mitigation plan"
                placeholderTextColor="#94A3B8"
                value={risk.mitigation}
                onChangeText={text => setRisk({ ...risk, mitigation: text })}
                multiline
                numberOfLines={3}
                textAlignVertical="top"
              />
            </View>

            <TouchableOpacity style={styles.modalActionButton} onPress={handleAddRisk}>
              <Text style={styles.modalActionButtonText}>Add Risk</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      {/* Edit Risk Modal */}
      <Modal
        animationType="slide"
        transparent={true}
        visible={showEditRiskModal}
        onRequestClose={() => setShowEditRiskModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Edit Risk</Text>
              <TouchableOpacity style={styles.modalCloseButton} onPress={() => setShowEditRiskModal(false)}>
                <Ionicons name="close" size={24} color="#64748B" />
              </TouchableOpacity>
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>Description *</Text>
              <TextInput
                style={[styles.input, styles.textArea]}
                placeholder="Enter risk description"
                placeholderTextColor="#94A3B8"
                value={risk.description}
                onChangeText={text => setRisk({ ...risk, description: text })}
                multiline
                numberOfLines={3}
                textAlignVertical="top"
              />
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>Severity</Text>
              <View style={styles.optionsContainer}>
                {severityOptions.map(severity => (
                  <TouchableOpacity
                    key={severity}
                    style={[
                      styles.optionButton,
                      risk.severity === severity && styles.optionButtonActive,
                      risk.severity === severity && { borderColor: getSeverityColor(severity) },
                    ]}
                    onPress={() => setRisk({ ...risk, severity: severity })}
                  >
                    <View style={[styles.severityDot, { backgroundColor: getSeverityColor(severity) }]} />
                    <Text
                      style={[
                        styles.optionButtonText,
                        risk.severity === severity && styles.optionButtonTextActive,
                        risk.severity === severity && { color: getSeverityColor(severity) },
                      ]}
                    >
                      {severity}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>Mitigation Plan *</Text>
              <TextInput
                style={[styles.input, styles.textArea]}
                placeholder="Enter mitigation plan"
                placeholderTextColor="#94A3B8"
                value={risk.mitigation}
                onChangeText={text => setRisk({ ...risk, mitigation: text })}
                multiline
                numberOfLines={3}
                textAlignVertical="top"
              />
            </View>

            <TouchableOpacity style={styles.modalActionButton} onPress={handleEditRisk}>
              <Text style={styles.modalActionButtonText}>Update Risk</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      {/* Confirm Delete Modal */}
      <Modal
        animationType="fade"
        transparent={true}
        visible={showConfirmDeleteModal}
        onRequestClose={() => setShowConfirmDeleteModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.confirmModalContent}>
            <MaterialCommunityIcons name="alert-circle-outline" size={40} color="#EF4444" />
            <Text style={styles.confirmModalTitle}>Delete Risk?</Text>
            <Text style={styles.confirmModalText}>
              Are you sure you want to delete this risk? This action cannot be undone.
            </Text>
            <View style={styles.confirmModalButtons}>
              <TouchableOpacity style={styles.cancelModalButton} onPress={() => setShowConfirmDeleteModal(false)}>
                <Text style={styles.cancelModalButtonText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.deleteModalButton} onPress={handleDeleteRisk}>
                <Text style={styles.deleteModalButtonText}>Delete</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: { flex: 1, backgroundColor: '#4A90E2' },
  header: {
    backgroundColor: '#4A90E2',
    paddingVertical: 15,
    paddingHorizontal: 20,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backButton: { padding: 5 },
  headerCenter: { flexDirection: 'row', alignItems: 'center' },
  headerLogo: { width: 30, height: 30, borderRadius: 15, resizeMode: 'cover' },
  headerTitle: { color: '#fff', fontSize: 20, fontWeight: 'bold', marginLeft: 10 },
  loadingContainer: { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: '#f1f5f9' },
  loadingText: { marginTop: 10, fontSize: 16, color: '#64748B' },
  projectHeader: {
    backgroundColor: '#fff',
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
  },
  projectTitle: { fontSize: 16, fontWeight: '600', color: '#0f172a' },
  riskSummary: {
    backgroundColor: '#fff',
    paddingVertical: 16,
    paddingHorizontal: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
  },
  riskStatRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 12 },
  riskStat: { flex: 1, borderRadius: 8, paddingVertical: 12, alignItems: 'center', marginHorizontal: 4 },
  riskStatValue: { fontSize: 18, fontWeight: '700' },
  riskStatLabel: { fontSize: 12, fontWeight: '500', marginTop: 4 },
  riskStatTotal: { alignItems: 'center', marginTop: 8 },
  riskStatTotalValue: { fontSize: 24, fontWeight: '700', color: '#0e4e96' },
  riskStatTotalLabel: { fontSize: 14, color: '#64748B' },
  searchFilterContainer: {
    backgroundColor: '#fff',
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f1f5f9',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    marginBottom: 12,
  },
  searchInput: { flex: 1, fontSize: 14, color: '#0f172a', marginLeft: 8, paddingVertical: 0 },
  filterContainer: { flexDirection: 'row', paddingVertical: 4 },
  filterOption: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 16,
    marginRight: 8,
    backgroundColor: '#f1f5f9',
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  filterOptionActive: { backgroundColor: '#0e4e96', borderColor: '#0e4e96' },
  filterOptionText: { fontSize: 12, color: '#64748B' },
  filterOptionTextActive: { color: '#fff', fontWeight: '600' },
  severityDot: { width: 8, height: 8, borderRadius: 4, marginRight: 6 },
  addButtonContainer: {
    backgroundColor: '#fff',
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
  },
  addButton: {
    backgroundColor: '#0e4e96',
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 16,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  addButtonText: { color: '#fff', fontSize: 14, fontWeight: '600', marginLeft: 8 },
  scrollContainer: { flex: 1, backgroundColor: '#f1f5f9' },
  contentContainer: { padding: 16, paddingBottom: 32 },
  emptyState: { alignItems: 'center', justifyContent: 'center', padding: 40, backgroundColor: '#fff', borderRadius: 12, marginTop: 16 },
  emptyStateText: { fontSize: 14, color: '#64748B', marginTop: 8 },
  riskCard: { backgroundColor: '#fff', borderRadius: 12, padding: 16, marginBottom: 12, shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.05, shadowRadius: 10, elevation: 2 },
  riskHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
  severityBadge: { paddingVertical: 4, paddingHorizontal: 8, borderRadius: 4 },
  severityText: { fontSize: 12, fontWeight: '500' },
  riskActions: { flexDirection: 'row' },
  riskAction: { padding: 4, marginLeft: 8 },
  riskDescription: { fontSize: 14, color: '#0f172a', marginBottom: 12, lineHeight: 20 },
  riskMitigation: { backgroundColor: '#f8fafc', borderRadius: 8, padding: 12 },
  riskMitigationLabel: { fontSize: 14, fontWeight: '500', color: '#334155', marginBottom: 4 },
  riskMitigationText: { fontSize: 14, color: '#64748B', lineHeight: 20 },
  modalOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'center', alignItems: 'center' },
  modalContent: { backgroundColor: '#fff', borderRadius: 12, width: '90%', padding: 20, maxHeight: '80%' },
  modalHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  modalTitle: { fontSize: 18, fontWeight: '600', color: '#0f172a' },
  modalCloseButton: { padding: 4 },
  inputGroup: { marginBottom: 16 },
  inputLabel: { fontSize: 14, fontWeight: '500', color: '#334155', marginBottom: 8 },
  input: { backgroundColor: '#f1f5f9', borderRadius: 8, paddingHorizontal: 12, paddingVertical: 10, fontSize: 16, color: '#0f172a', borderWidth: 1, borderColor: '#e2e8f0' },
  textArea: { minHeight: 100, paddingTop: 12, textAlignVertical: 'top' },
  optionsContainer: { flexDirection: 'row', flexWrap: 'wrap', marginHorizontal: -4 },
  optionButton: { flexDirection: 'row', alignItems: 'center', paddingVertical: 8, paddingHorizontal: 12, borderRadius: 8, marginHorizontal: 4, marginBottom: 8, backgroundColor: '#f1f5f9', borderWidth: 1, borderColor: '#e2e8f0' },
  optionButtonActive: { backgroundColor: '#fff', borderWidth: 1 },
  optionButtonText: { fontSize: 14, color: '#64748B' },
  optionButtonTextActive: { fontWeight: '600' },
  modalActionButton: { backgroundColor: '#0e4e96', borderRadius: 8, paddingVertical: 12, alignItems: 'center', justifyContent: 'center' },
  modalActionButtonText: { fontSize: 16, fontWeight: '600', color: '#fff' },
  confirmModalContent: { backgroundColor: '#fff', borderRadius: 12, width: '90%', padding: 20, alignItems: 'center' },
  confirmModalTitle: { fontSize: 18, fontWeight: '600', color: '#0f172a', marginTop: 12, marginBottom: 8 },
  confirmModalText: { fontSize: 14, color: '#64748B', textAlign: 'center', marginBottom: 20 },
  confirmModalButtons: { flexDirection: 'row', width: '100%' },
  cancelModalButton: { flex: 1, paddingVertical: 12, alignItems: 'center', justifyContent: 'center', marginRight: 8, borderWidth: 1, borderColor: '#e2e8f0', borderRadius: 8 },
  cancelModalButtonText: { fontSize: 14, fontWeight: '600', color: '#64748B' },
  deleteModalButton: { flex: 1, paddingVertical: 12, alignItems: 'center', justifyContent: 'center', marginLeft: 8, backgroundColor: '#EF4444', borderRadius: 8 },
  deleteModalButtonText: { fontSize: 14, fontWeight: '600', color: '#fff' },
});